import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const BranchContext = createContext(null);

export function BranchProvider({ children }) {
  const { user } = useAuth();
  const [allBranches, setAllBranches] = useState([]);
  const key = `pp_branch_${user?.id || "anon"}`;
  const [activeBranch, setActiveBranch] = useState("all");
  useEffect(() => { setActiveBranch(localStorage.getItem(key) || "all"); }, [key]);

  useEffect(() => {
    if (!user) return;
    api.get("/branches").then((r) => setAllBranches(r.data)).catch(() => {});
  }, [user]);

  const isSuper = !!user && (user.permissions || []).includes("*");
  const myIds = (user && user.branch_ids) || [];
  // A user is branch-restricted when they are not a superuser and have assigned branches.
  const restricted = !!user && !isSuper && myIds.length > 0;

  // Restricted users only ever see their own assigned locations — everywhere.
  const scoped = restricted ? allBranches.filter((b) => myIds.includes(b.id)) : allBranches;
  const branches = scoped.filter((b) => b.is_active !== false);

  // Force a valid, in-scope selection for restricted users (never the global "all").
  useEffect(() => {
    if (!restricted) return;
    if (activeBranch === "all" || !myIds.includes(activeBranch)) {
      const first = branches[0]?.id || myIds[0];
      if (first && first !== activeBranch) {
        setActiveBranch(first);
        localStorage.setItem(key, first);
      }
    }
  }, [restricted, activeBranch, branches.length]); // eslint-disable-line

  const changeBranch = (id) => {
    if (restricted && (id === "all" || !myIds.includes(id))) return; // block out-of-scope
    setActiveBranch(id);
    localStorage.setItem(key, id);
  };

  // branch_id query param helper ("all" -> undefined)
  const branchParam = activeBranch === "all" ? undefined : activeBranch;

  // Unscoped active branches / warehouses — needed as valid transfer sources & destinations
  // even for a restricted branch (e.g. requesting stock FROM a warehouse). No stock data is exposed here.
  const allActive = allBranches.filter((b) => b.is_active !== false);
  const warehouses = allActive.filter((b) => (b.roles || []).includes("warehouse") || b.type === "warehouse");

  return (
    <BranchContext.Provider value={{ branches, allBranches: scoped, allActive, warehouses, activeBranch, changeBranch, branchParam, restricted, setBranches: setAllBranches }}>
      {children}
    </BranchContext.Provider>
  );
}

export const useBranch = () => useContext(BranchContext);
